import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Message as MessageIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  HowToReg as HowToRegIcon,
  ContactMail as ContactMailIcon,
  PersonAdd as PersonAddIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { EmptyState } from './primitives';
import { isTeacher, isMasterAdmin, isAdminRole } from '../../constants/roles';

// Common props for a non-semantic element (Typography/Box/TableRow) acting
// as a link — every "quiet" clickable row on this page (the safety line,
// a by-school row, a today's-sessions row, an onboarding tile) is plain
// text/layout markup, not a real <a>/<button>, so keyboard reachability and
// activation have to be added by hand rather than coming free the way they
// would on a real Button/Link.
function linkRowProps(onClick) {
  return {
    role: 'button',
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
  };
}

const FOCUS_VISIBLE_SX = {
  '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
};

function formatSlotTime(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = String(hhmm).split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

// KPI tile — same visual language DashboardView's old inline version used
// (icon chip + label-over-value), pulled out here since OverviewPage now
// renders more than four of them.
function KpiCard({ label, value, icon, onClick, tip, span }) {
  return (
    <Grid size={span}>
      <Card
        onClick={onClick || undefined}
        sx={{
          borderRadius: radius.surface,
          boxShadow: 'none',
          border: '1px solid rgba(24,24,27,0.08)',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'border-color 0.15s ease, transform 0.15s ease',
          height: '100%',
          ...(onClick ? TOUCH_HIT_SX : {}),
          '&:hover': onClick ? { borderColor: adminColors.accentBorder, transform: 'translateY(-1px)' } : {},
        }}
        aria-label={tip}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: radius.surface, // icon badge — matches primitives/EmptyState.js's own icon-box radius
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: adminColors.surface,
              color: adminColors.inkSoft,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" sx={{ color: adminColors.inkSoft, fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3, mb: 0.25 }}>
              {label}
            </Typography>
            <Typography component="div" sx={{ color: adminColors.ink, fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Card sx={{ borderRadius: radius.surface, boxShadow: 'none', border: '1px solid rgba(24,24,27,0.08)', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: adminColors.ink, fontWeight: 600 }}>{title}</Typography>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

// /admin index route, replacing the old inline DashboardView. Reads
// `stats` straight from AdminDashboardV2's already-fetched GET
// /api/dashboard/stats response (no second fetch) — every number here is
// something that endpoint actually returns; there is no prior-period data
// to compute a trend/delta from, so none is shown or invented.
// `dashboardError`/`onRetry` are deliberately not handled here — a failed
// GET /api/dashboard/stats already surfaces as a dismissable, page-wide
// banner+Retry above every route (see AdminDashboardV2's own `dashboardError`
// block, rendered once above `renderRoutes()`, not per-view). Duplicating
// that here would just stack two identical error banners on this one route;
// this component instead degrades quietly (every number falls back to '—'
// via the `stats?.` reads below) and lets that single shared banner own the
// error state and the retry action.
export default function OverviewPage({ stats, loading, currentUser }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTeacherDashboard = isTeacher(currentUser?.role);
  const isMaster = isMasterAdmin(currentUser?.role);
  const isAdmin = isAdminRole(currentUser?.role);

  const pendingFlags = stats?.active_safety_flags ?? null;
  const hasPendingFlags = typeof pendingFlags === 'number' && pendingFlags > 0;
  const flagsUnavailable = pendingFlags === null;

  const bySchool = stats?.by_school || [];
  const sessionsToday = stats?.sessions_today || [];
  const onboarding = stats?.onboarding || null;

  // Students KPI — exact, role-scoped count from the backend
  // (get_dashboard_stats), linked to /admin/students. Counts every STUDENT
  // row in the viewer's scope regardless of whether `school` is set, so
  // (unlike summing the `by_school` table below) it never misses a
  // schoolless student.
  const studentsTotal = stats ? stats.total_students ?? null : null;

  // Staff KPI (admins only — a TEACHER has no /admin/staff page to link
  // to). Also an exact, role-scoped count from the backend: every role in
  // the viewer's scope that isn't STUDENT (TEACHER, SCHOOL_ADMIN,
  // MASTER_ADMIN, and legacy "admin").
  const staffTotal = stats && isAdmin ? stats.total_staff ?? null : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: adminColors.surface, minHeight: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ color: adminColors.ink, fontWeight: 600, mb: 3 }}>
        Dashboard Overview
      </Typography>

      {/* Lead tile: safety leads full-width, same as before — it's the
          decision this page exists to surface first. */}
      <Card
        sx={{
          mb: 3,
          borderRadius: radius.surface,
          boxShadow: 'none',
          border: `1px solid ${
            flagsUnavailable ? 'rgba(24,24,27,0.08)' : hasPendingFlags ? alpha(theme.palette.error.main, 0.3) : adminColors.accentBorder
          }`,
          backgroundColor: flagsUnavailable ? adminColors.surfaceRaised : hasPendingFlags ? alpha(theme.palette.error.main, 0.05) : adminColors.accentLight,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, '&:last-child': { pb: { xs: 2.5, sm: 3.5 } } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: radius.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  backgroundColor: flagsUnavailable ? 'rgba(24,24,27,0.06)' : hasPendingFlags ? alpha(theme.palette.error.main, 0.12) : alpha(theme.palette.success.main, 0.14),
                  color: flagsUnavailable ? adminColors.inkSoft : hasPendingFlags ? theme.palette.error.main : theme.palette.success.main,
                }}
              >
                {flagsUnavailable ? <SecurityIcon sx={{ fontSize: 24 }} /> : hasPendingFlags ? <FlagIcon sx={{ fontSize: 24 }} /> : <CheckCircleIcon sx={{ fontSize: 24 }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: hasPendingFlags ? theme.palette.error.dark : adminColors.ink, lineHeight: 1.3 }}>
                  {flagsUnavailable ? 'Safety status unavailable' : hasPendingFlags ? `${pendingFlags} ${pendingFlags === 1 ? 'flag needs' : 'flags need'} review` : 'All clear — no pending safety flags'}
                </Typography>
                <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.5, maxWidth: 480 }}>
                  {flagsUnavailable
                    ? "Couldn't load the safety count just now — open Safety Monitoring to check directly."
                    : hasPendingFlags
                    ? 'Flagged student conversations are waiting on an admin decision.'
                    : 'No student conversations are currently flagged. New flags will show up here as soon as they’re detected.'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant={hasPendingFlags ? 'contained' : 'outlined'}
              onClick={() => navigate('/admin/safety?status=PENDING')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                ...TOUCH_HIT_SX,
                flexShrink: 0,
                alignSelf: { xs: 'stretch', sm: 'center' },
                backgroundColor: hasPendingFlags ? theme.palette.error.main : 'transparent',
                color: hasPendingFlags ? '#fff' : adminColors.ink,
                borderColor: hasPendingFlags ? 'transparent' : adminColors.accentBorder,
                '&:hover': {
                  backgroundColor: hasPendingFlags ? theme.palette.error.dark : adminColors.accentLight,
                  borderColor: hasPendingFlags ? 'transparent' : adminColors.accent,
                },
              }}
            >
              {hasPendingFlags ? 'Review flags' : 'Open Safety Monitoring'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <KpiCard
          label={isTeacherDashboard ? 'My Students' : 'Students'}
          value={studentsTotal ?? '—'}
          icon={<PeopleIcon sx={{ fontSize: 22 }} />}
          onClick={() => navigate('/admin/students')}
          tip={isTeacherDashboard ? 'View my students' : 'View all students'}
          span={{ xs: 12, sm: 6, md: 3 }}
        />
        {isAdmin && (
          <KpiCard
            label="Staff"
            value={staffTotal ?? '—'}
            icon={<BadgeIcon sx={{ fontSize: 22 }} />}
            onClick={() => navigate('/admin/staff')}
            tip="View staff"
            span={{ xs: 12, sm: 6, md: 3 }}
          />
        )}
        <KpiCard
          label="Active students · 7 days"
          value={stats?.active_students_7d ?? '—'}
          icon={<TrendingUpIcon sx={{ fontSize: 22 }} />}
          span={{ xs: 12, sm: 6, md: 3 }}
        />
        {!isTeacherDashboard && (
          <KpiCard
            label="Total Schools"
            value={stats?.total_schools ?? '—'}
            icon={<SchoolIcon sx={{ fontSize: 22 }} />}
            span={{ xs: 12, sm: 6, md: 3 }}
          />
        )}
        <KpiCard
          label="Total Messages"
          value={stats?.total_messages != null ? stats.total_messages.toLocaleString() : '—'}
          icon={<MessageIcon sx={{ fontSize: 22 }} />}
          onClick={() => navigate('/admin/conversations')}
          tip="View conversations"
          span={{ xs: 12, sm: 6, md: isTeacherDashboard ? 6 : 3 }}
        />
      </Grid>

      {/* One quiet safety line, always visible regardless of the lead tile
          above (which reads differently once flags exist) — a direct link
          to the pre-filtered pending queue. */}
      <Typography
        variant="body2"
        {...linkRowProps(() => navigate('/admin/safety?status=PENDING'))}
        sx={{
          color: adminColors.inkSoft,
          mb: 3,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: radius.control,
          '&:hover': { color: adminColors.accent, textDecoration: 'underline' },
          ...FOCUS_VISIBLE_SX,
        }}
      >
        {flagsUnavailable ? 'Safety status unavailable' : `${pendingFlags} flag${pendingFlags === 1 ? '' : 's'} waiting for review`}
        <ArrowForwardIcon sx={{ fontSize: 14 }} />
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* By school — master admin only; school-scoped roles and teachers
            already see exactly one school's worth of everything else on
            this page, so a one-row "by school" table would be redundant. */}
        {isMaster && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="By school">
              {bySchool.length === 0 ? (
                <EmptyState title="No schools yet" description="Schools will show up here once classes or students exist." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: adminColors.surface }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: adminColors.ink }}>School</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: adminColors.ink }}>Students</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: adminColors.ink }}>Classes</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: adminColors.ink }}>Active · 7d</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: adminColors.ink }}>Flags</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bySchool.map((row) => (
                        <TableRow
                          key={row.school}
                          hover
                          {...linkRowProps(() => navigate(`/admin/students?school=${encodeURIComponent(row.school)}`))}
                          sx={{ cursor: 'pointer', ...FOCUS_VISIBLE_SX }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: adminColors.ink }}>{row.school}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.students}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.classes}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.active_7d}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: row.pending_flags > 0 ? theme.palette.error.main : adminColors.ink, fontWeight: row.pending_flags > 0 ? 700 : 400 }}>
                            {row.pending_flags}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </Grid>
        )}

        {/* Today's sessions — folds in what the old Today/Week schedule
            widget showed for "today", each row linking straight to that
            class's detail page instead of just naming it. The Week grid
            view is dropped: this same information (which classes meet,
            when) now lives one click away on the Classes page's per-class
            schedule, so keeping a second, read-only copy of it here would
            just be the same data twice. */}
        <Grid size={{ xs: 12, md: isMaster ? 6 : 12 }}>
          <SectionCard title="Today's sessions">
            {sessionsToday.length === 0 ? (
              <EmptyState title="Nothing scheduled today" description="No class has a time slot today." />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {[...sessionsToday]
                  .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                  .map((s, i, arr) => (
                    <Box
                      key={`${s.group_id}-${s.start_time}`}
                      {...linkRowProps(() => navigate(`/admin/classes/${s.group_id}`))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1.5,
                        px: 1,
                        mx: -1,
                        borderRadius: radius.control,
                        cursor: 'pointer',
                        borderBottom: i < arr.length - 1 ? '1px solid rgba(24,24,27,0.06)' : 'none',
                        '&:hover': { backgroundColor: adminColors.surface },
                        ...TOUCH_HIT_SX,
                        ...FOCUS_VISIBLE_SX,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: adminColors.accent, fontVariantNumeric: 'tabular-nums', minWidth: 118, flexShrink: 0 }}>
                        {formatSlotTime(s.start_time)}–{formatSlotTime(s.end_time)}
                      </Typography>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }} noWrap>
                          {s.name || `Class #${s.group_id}`}
                        </Typography>
                        {isMaster && (
                          <Typography variant="caption" sx={{ color: adminColors.inkSoft }} noWrap>
                            {s.school}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Onboarding funnel */}
      {onboarding && (
        <SectionCard title="Onboarding">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                {...linkRowProps(() => navigate('/admin/invites'))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: radius.control, cursor: 'pointer', '&:hover': { backgroundColor: adminColors.surface }, ...TOUCH_HIT_SX, ...FOCUS_VISIBLE_SX }}
              >
                <HowToRegIcon sx={{ color: adminColors.inkSoft }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
                    {onboarding.invites_pending} pending{onboarding.invites_failed > 0 ? ` · ${onboarding.invites_failed} failed` : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>Email invites</Typography>
                </Box>
              </Box>
            </Grid>
            {isMaster && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box
                  {...linkRowProps(() => navigate('/admin/requests'))}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: radius.control, cursor: 'pointer', '&:hover': { backgroundColor: adminColors.surface }, ...TOUCH_HIT_SX, ...FOCUS_VISIBLE_SX }}
                >
                  <ContactMailIcon sx={{ color: adminColors.inkSoft }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
                      {onboarding.signup_requests_new} new
                    </Typography>
                    <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>Sign-up requests</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                <PersonAddIcon sx={{ color: adminColors.inkSoft }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
                    {onboarding.students_added_7d}
                  </Typography>
                  <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>Students added · 7 days</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {loading && !stats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>Loading…</Typography>
        </Box>
      )}
    </Box>
  );
}
